import { logger } from "../../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { Market, MarketOption, DbMarketType } from "../../types.ts";
import { supabase } from "../../lib/db.ts";
import { preserveLocalDateTime } from "../../utils/dateUtils.ts";
import { fetchMarketCategories, Category as CategoryType } from "../../services/categoryService.ts";
import { toast } from 'react-hot-toast';
import {
  Loader2, AlertTriangle, Save, Plus, Trash2,
  Tag as CategoryIcon, Image as ImageIcon, X,
  AlertCircle,
  Info,
  Clipboard,
  RefreshCw
} from 'lucide-react';

// Define expected props
interface MarketEditorProps {
  market: Market;
  marketId?: string;
  onMarketUpdate?: () => void;
  canEdit: boolean;
}

// Define the function locally
const formatDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 16);
  } catch { return ''; }
};

export function MarketEditor({ market, marketId, onMarketUpdate, canEdit }: MarketEditorProps) {
  const [formData, setFormData] = useState({
    title: market.title || '',
    description: market.description || '',
    market_type: market.market_type || 'fixed' as DbMarketType,
    resolution_time: market.resolution_time || undefined,
    market_options: market.market_options || [],
    is_private: market.is_private ?? false,
    category: market.category,
    image_url: market.image_url || undefined,
    trading_end_time: market.trading_end_time ?? null,
    short_code: market.short_code || null,
  });
  
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(market.image_url || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
    };
    loadCategories();
  }, []);

  // Update formData if the market prop changes
  useEffect(() => {
    setFormData({
      title: market.title || '',
      description: market.description || '',
      market_type: market.market_type || 'fixed',
      resolution_time: market.resolution_time || undefined,
      market_options: market.market_options || [],
      is_private: market.is_private ?? false,
      category: market.category,
      image_url: market.image_url || undefined,
      trading_end_time: market.trading_end_time ?? null,
      short_code: market.short_code || null,
    });
    setImagePreview(market.image_url || null);
    setImageFile(null);
  }, [market]);

  // --- Input Handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      const numValue = value === '' ? undefined : Number(value);
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else if (type === 'datetime-local') {
      // Handle datetime inputs with timezone preservation
      const convertedValue = preserveLocalDateTime(value);
      setFormData(prev => ({
        ...prev,
        [name]: convertedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setUpdateError(null);
  };

  // --- Options Handlers --- 
  const handleOptionChange = (optionId: number, field: keyof MarketOption, value: string) => {
    setFormData(prev => ({
      ...prev,
      market_options: prev.market_options?.map((opt: MarketOption) => {
        if (opt.id === optionId) {
          return { ...opt, [field]: value };
        }
        return opt;
      }) || []
    }));
  };

  const handleAddOption = () => {
    if (formData.market_options && formData.market_options.length >= 20) {
      toast.error("Maximum of 20 options allowed per market.");
      return;
    }

    const newOption: MarketOption = {
      id: -Date.now(),
      market_id: marketId || '',
      name: '',
      status: 'active',
      trading_end_time: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      market_options: [...(prev.market_options || []), newOption]
    }));
  };

  const handleRemoveOption = async (optionId: number) => {
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }

    // For new options (temporary IDs), just remove from local state
    if (optionId < 0) {
      setFormData(prev => ({
        ...prev,
        market_options: prev.market_options?.filter((opt: MarketOption) => opt.id !== optionId) || []
      }));
      return;
    }

    // For existing options (positive IDs), use the database function
    try {
      const { error } = await supabase.rpc('delete_market_option', {
        p_market_id: marketId,
        p_option_id_to_delete: optionId
      });

      if (error) throw error;

      setFormData(prev => ({
        ...prev,
        market_options: prev.market_options?.filter((opt: MarketOption) => opt.id !== optionId) || []
      }));

      toast.success('Option deleted successfully!');
      if (onMarketUpdate) onMarketUpdate();

    } catch (error: unknown) {
      logger.error("Error deleting option:", error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Could not delete option. Please try again.");
      }
    }
  };

  // --- Image Handlers ---
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image_url: undefined
    }));
    setImagePreview(null);
    setImageFile(null);
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `market-${marketId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `market-covers/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('market-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('market-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // --- Short Code Handler ---
  const handleGenerateCode = async () => {
    if (!marketId) {
      toast.error("Market ID is missing.");
      return;
    }
    if (!formData.is_private) {
      toast.error("Short codes are only for private markets.");
      return;
    }

    setIsGeneratingCode(true);
    const toastId = toast.loading('Generating unique code...');

    try {
      const { data: generatedCode, error } = await supabase.rpc('generate_market_short_code', {
        p_market_id: marketId
      });

      if (error) {
        throw error;
      }

      if (generatedCode) {
        toast.success(`Generated code: ${generatedCode}`, { id: toastId });
        // Update local form state immediately
        setFormData(prev => ({
          ...prev,
          short_code: generatedCode
        }));
      } else {
         throw new Error("Failed to generate code (RPC returned no data).");
      }

    } catch (error: unknown) {
      logger.error("Error generating short code:", error);
      const message = error instanceof Error ? error.message : "Could not generate short code.";
      toast.error(`Error: ${message}`, { id: toastId });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // --- Copy to Clipboard Handler ---
  const handleCopyToClipboard = (text: string | null | undefined) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Code copied to clipboard!'))
      .catch(err => {
        logger.error('Failed to copy text: ', err);
        toast.error('Failed to copy code.');
      });
  };

  // --- Save Handler --- 
  const handleSaveChanges = async () => {
    if (!marketId) {
      toast.error("Cannot save: Missing market ID.");
      return;
    }

    // Validate that we have at least 2 options
    if (!formData.market_options || formData.market_options.length < 2) {
      toast.error("Market must have at least 2 options.");
      return;
    }

    // Validate that all options have names
    const optionsWithNames = formData.market_options.filter(opt => opt.name.trim());
    if (optionsWithNames.length < 2) {
      toast.error("All market options must have names.");
      return;
    }

    // Check for duplicate names
    const nameCounts = new Map<string, number>();
    for (const option of optionsWithNames) {
      const trimmedName = option.name.trim().toLowerCase();
      nameCounts.set(trimmedName, (nameCounts.get(trimmedName) || 0) + 1);
    }
    
    const duplicateNames = Array.from(nameCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
    
    if (duplicateNames.length > 0) {
      toast.error(`Please fix duplicate option names: ${duplicateNames.join(', ')}`);
      return;
    }

    setIsUpdating(true);

    try {
      // Upload image if a new one was selected
      let imageUrl = formData.image_url;
      if (imageFile) {
        toast.loading('Uploading image...', { id: 'image-upload' });
        try {
          imageUrl = await uploadImage(imageFile);
          toast.success('Image uploaded successfully!', { id: 'image-upload' });
          setFormData(prev => ({
            ...prev,
            image_url: imageUrl
          }));
          setImageFile(null);
          setImagePreview(imageUrl);
        } catch (error) {
          logger.error('Failed to upload image:', error);
          toast.error(error instanceof Error ? error.message : 'Failed to upload image', { id: 'image-upload' });
          throw error;
        }
      }

      // Call the creator-specific update function
      const { error: detailsError } = await supabase.rpc('update_market_details_creator', {
        p_market_id: marketId,
        p_title: formData.title,
        p_description: formData.description,
        p_category: formData.category,
        p_market_type: formData.market_type,
        p_is_private: formData.is_private,
        p_short_code: formData.short_code,
        p_trading_end_time: null,
        p_resolution_time: formData.resolution_time?.trim() ? formData.resolution_time : null,
        p_image_url: imageUrl
      });

      if (detailsError) throw detailsError;

      // Handle new options (negative IDs) - insert them
      const newOptions = optionsWithNames.filter(opt => opt.id < 0);
      if (newOptions.length > 0) {
        const insertData = newOptions.map(option => ({
          market_id: marketId,
          name: option.name.trim(),
          trading_end_time: null,
          status: option.status || 'active'
        }));

        const { error: insertError } = await supabase
          .from('market_options')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      // Handle existing options (positive IDs) - update them
      const existingOptions = optionsWithNames.filter(opt => opt.id > 0);
      for (const option of existingOptions) {
        const { error: updateError } = await supabase
          .from('market_options')
          .update({
            name: option.name.trim(),
            trading_end_time: null,
            status: option.status || 'active',
            updated_at: new Date().toISOString()
          })
          .eq('id', option.id);

        if (updateError) throw updateError;
      }
      
      toast.success('Market updated successfully!');
      
      if (onMarketUpdate) onMarketUpdate();

    } catch (err) {
      const error = err as { message: string };
      logger.error('Failed to update market:', error);
      toast.error(error.message || 'An error occurred while saving.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to check if an option name is duplicate
  const isDuplicateOptionName = (currentId: number, name: string): boolean => {
    if (!name.trim()) return false;
    
    const trimmedName = name.trim().toLowerCase();
    return formData.market_options?.some((opt: MarketOption) => 
      opt.id !== currentId && opt.name.trim().toLowerCase() === trimmedName
    ) || false;
  };

  // Helper function to get all duplicate names for display
  const getDuplicateOptionNames = (): string[] => {
    const nameCounts = new Map<string, number>();
    const duplicates: string[] = [];
    
    formData.market_options?.forEach(option => {
      const trimmedName = option.name.trim().toLowerCase();
      if (trimmedName) {
        const count = nameCounts.get(trimmedName) || 0;
        nameCounts.set(trimmedName, count + 1);
        if (count === 1) {
          duplicates.push(option.name.trim());
        }
      }
    });
    
    return duplicates;
  };

  return (
    <div className="space-y-8 bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Edit Your Market</h2>
        {!canEdit && (
          <div className="text-sm text-gray-500">
            <Info className="w-4 h-4 inline mr-1" />
            Market editing is currently disabled
          </div>
        )}
      </div>

      {/* Notice for creators */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <Info className="w-4 h-4 inline mr-1" />
          You can edit your own markets. Public markets may require admin approval before appearing in listings.
        </p>
      </div>

      {/* Section 1: Core Information */}
      <section>
        <h3 className="text-lg font-medium mb-4 text-gray-800">Core Information</h3>
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Market Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description/Rules</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
            <div className="relative">
              <CategoryIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              <select
                id="category"
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                className="w-full pl-10 p-2 border border-gray-300 rounded-md bg-white appearance-none disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={categories.length === 0 || !canEdit}
              >
                <option value="" disabled>Select Category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Image Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Market Image</label>
            <div className={`space-y-4 ${!canEdit ? 'opacity-50' : ''}`}>
              {imagePreview && (
                <div className="relative w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Market preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  {canEdit && (
                     <button
                       type="button"
                       onClick={handleRemoveImage}
                       className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                       aria-label="Remove image"
                       disabled={!canEdit}
                     >
                       <X className="w-4 h-4" />
                     </button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-4">
                <label className={`flex items-center gap-2 px-4 py-2 text-white rounded-md transition-colors ${canEdit ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}>
                  <ImageIcon className="w-5 h-5" />
                  <span>{imagePreview ? 'Change Image' : 'Upload Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={!canEdit}
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500">
                Recommended size: 1200x630px. Max file size: 5MB.
              </p>
            </div>
          </div>

          {/* Market Privacy */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Market Visibility</label>
            <div className="space-y-3">
              <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                !formData.is_private 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 bg-white hover:border-gray-400'
              } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="is_private"
                  checked={!formData.is_private}
                  onChange={() => setFormData(prev => ({ ...prev, is_private: false }))}
                  className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  disabled={!canEdit}
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">🌍 Public Market</span>
                  <p className="text-xs text-gray-600 mt-1">
                    Visible to everyone on Wayja after admin approval. Your market will be set to "Pending Approval" status and reviewed by admins before appearing in public listings.
                  </p>
                </div>
              </label>
              
              <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                formData.is_private 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-300 bg-white hover:border-gray-400'
              } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="is_private"
                  checked={formData.is_private}
                  onChange={() => setFormData(prev => ({ ...prev, is_private: true }))}
                  className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  disabled={!canEdit}
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">🔒 Private Market</span>
                  <p className="text-xs text-gray-600 mt-1">
                    Only accessible via direct link. Perfect for private groups or events.
                  </p>
                </div>
              </label>
            </div>

            {/* Public Market Notice */}
            {!formData.is_private && (
              <div className="mt-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Admin Approval Required</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Your market will be set to "Pending Approval" status and needs to be reviewed by an admin before it appears in public market listings. Private markets are active immediately.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Short Code Section (shown only for private markets) */}
            {formData.is_private && (
              <div className={`mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 ${!canEdit ? 'opacity-50' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Private Access Code
                </label>
                {formData.short_code ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 border border-gray-300 rounded-md bg-white">
                      <span className="font-mono text-sm text-gray-800 flex-grow">
                        {formData.short_code}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopyToClipboard(formData.short_code)}
                        className="p-1.5 text-gray-500 rounded-md hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                        title="Copy code"
                        disabled={!canEdit}
                      >
                        <Clipboard className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Share this code with others to allow them to find your private market.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={handleGenerateCode}
                      disabled={isGeneratingCode || !canEdit}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingCode ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" /> 
                      )}
                      <span>{isGeneratingCode ? 'Generating...' : 'Generate Access Code'}</span>
                    </button>
                    <p className="text-xs text-gray-500">
                      Generate a unique code that others can use to find your private market.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: Market Structure */}
      <section className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-4 text-gray-800">Market Structure</h3>
        <div className="space-y-6">
           {/* Market Type */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Market Type</label>
             <div className="flex items-center space-x-6">
               <div className={`${!canEdit ? 'opacity-50' : ''}`}>
                 <label className={`flex items-center cursor-pointer ${!canEdit ? 'cursor-not-allowed' : ''}`}>
                   <input
                     type="radio"
                     name="market_type"
                     value="fixed"
                     checked={formData.market_type === 'fixed'}
                     onChange={handleInputChange}
                     className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                     disabled={!canEdit}
                   />
                   <span className="ml-2 text-sm text-gray-700">Standard Market</span>
                 </label>
                 <p className="ml-6 text-xs text-gray-500 mt-1">Trading continues until resolution.</p>
               </div>
               <div className={`${!canEdit ? 'opacity-50' : ''}`}>
                 <label className={`flex items-center cursor-pointer ${!canEdit ? 'cursor-not-allowed' : ''}`}>
                   <input
                     type="radio"
                     name="market_type"
                     value="knockout"
                     checked={formData.market_type === 'knockout'}
                     onChange={handleInputChange}
                     className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
                     disabled={!canEdit}
                   />
                   <span className="ml-2 text-sm text-gray-700">Knockout Market</span>
                 </label>
                 <p className="ml-6 text-xs text-gray-500 mt-1">Options can be eliminated over time until resolution.</p>
               </div>
             </div>
           </div>

           {/* Market Options Editor */} 
           <div className={`${!canEdit ? 'opacity-50' : ''}`}>
             <label className="block text-sm font-medium text-gray-700 mb-2">Market Options</label>
             
             {/* Duplicate names warning */}
             {(() => {
               const duplicateNames = getDuplicateOptionNames();
               
               if (duplicateNames.length > 0) {
                 return (
                   <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                     <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                     <div>
                       <p className="text-red-700 font-medium">Duplicate option names detected</p>
                       <p className="text-red-600 text-sm">
                         The following names are duplicated: {duplicateNames.map(name => `"${name}"`).join(', ')}. 
                         Each option must have a unique name.
                       </p>
                     </div>
                   </div>
                 );
               }
               return null;
             })()}
             
             <div className="space-y-3">
                 {formData.market_options?.map((option) => (
                     <div key={option.id} className="flex flex-col sm:flex-row items-start gap-2 p-2 border border-gray-200 rounded-md bg-gray-50">
                         <div className="flex-grow w-full">
                           <input
                             type="text"
                             value={option.name}
                             onChange={(e) => handleOptionChange(option.id, 'name', e.target.value)}
                             placeholder="Option Name"
                             className={`w-full p-1 border rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                               isDuplicateOptionName(option.id, option.name) 
                                 ? 'border-red-500 focus:ring-red-500' 
                                 : 'border-gray-300'
                             }`}
                             disabled={!canEdit}
                           />
                           {isDuplicateOptionName(option.id, option.name) && (
                             <p className="text-xs text-red-500 mt-1">
                               This name already exists in the market. Please use a unique name.
                             </p>
                           )}
                           {option.name.trim() && !isDuplicateOptionName(option.id, option.name) && (
                             <p className="text-xs text-green-500 mt-1">
                               ✓ Unique name
                             </p>
                           )}
                         </div>
                         {canEdit && (
                            <button
                              type="button"
                              onClick={() => handleRemoveOption(option.id)}
                              className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mt-1 sm:mt-0"
                              aria-label="Remove option"
                              disabled={formData.market_options?.length <= 2 || !canEdit} 
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                         )}
                     </div>
                 ))}
             </div>
             {canEdit && (
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="mt-3 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  disabled={!canEdit}
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
             )}
           </div>
        </div>
      </section>

      {/* Section 3: Timing */}
      <section className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium mb-4 text-gray-800">Timing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Resolution Time */} 
            <div>
              <label htmlFor="resolution_time" className="block text-sm font-medium text-gray-700 mb-1">
                Market resolution (optional)
              </label>
              <input
                type="datetime-local"
                id="resolution_time"
                name="resolution_time"
                value={formatDateTimeLocal(formData.resolution_time)}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md bg-white disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!canEdit}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for open-ended topics (shows as TBA). Add a date when the outcome timeline is confirmed.
              </p>
            </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="pt-8 border-t border-gray-200 flex justify-between items-center gap-4">
        <div className="flex-1">
          {updateError && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {updateError}
            </p>
          )}
          {!canEdit && (
            <p className="text-sm text-gray-500">
              {market.status === 'resolved' ? 'This market has been resolved and cannot be edited.' :
               market.status === 'cancelled' ? 'This market has been cancelled and cannot be edited.' :
               'Editing is currently disabled for this market.'}
            </p>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={isUpdating}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>
    </div>
  );
}

